import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

export const UTM_PARAMS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
] as const;

export const CLICK_ID_PARAMS = ['gclid', 'fbclid'] as const;

export type UtmParam = (typeof UTM_PARAMS)[number];
export type UtmAttribution = Partial<Record<UtmParam, string>>;

export type LandingAttribution = UtmAttribution & {
	gclid?: string;
	fbclid?: string;
	fbc?: string;
	landing_page?: string;
	referrer?: string;
};

export type SignupAttribution = LandingAttribution & {
	ga_client_id?: string;
	fbp?: string;
	consent_granted: boolean;
};

const ATTRIBUTION_KEY = 'lead_journey_attribution';
export const SIGNUP_ATTRIBUTION_COOKIE = 'lead_attribution';
const ATTRIBUTION_COOKIE = SIGNUP_ATTRIBUTION_COOKIE;
const JOURNEY_ID_KEY = 'lead_journey_id';
const QUALIFICATION_OUTCOME_KEY = 'lead_qualification_outcome';
const JOURNEY_CASE_ID_KEY = 'lead_journey_case_id';

type QueryRecord = Record<string, string | string[] | undefined>;

function isBrowser(): boolean {
	return typeof window !== 'undefined';
}

function parseQueryValue(
	value: string | string[] | undefined
): string | undefined {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

function looksLikePii(value: string): boolean {
	return value.includes('@') || /\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/.test(value);
}

function getCookie(name: string): string | undefined {
	if (!isBrowser()) return undefined;

	const prefix = `${name}=`;
	const match = document.cookie
		.split(';')
		.map((part) => part.trim())
		.find((part) => part.startsWith(prefix));

	if (!match) return undefined;

	const value = decodeURIComponent(match.slice(prefix.length));
	return value.length > 0 ? value : undefined;
}

function parseGaClientId(rawGaCookie: string | undefined): string | undefined {
	if (!rawGaCookie) return undefined;

	const parts = rawGaCookie.split('.');
	if (parts.length < 2) return undefined;

	return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
}

function parseLandingFromQuery(query: QueryRecord): LandingAttribution {
	const parsed: LandingAttribution = {};

	for (const param of UTM_PARAMS) {
		const value = parseQueryValue(query[param]);
		if (value && !looksLikePii(value)) {
			parsed[param] = value;
		}
	}

	for (const param of CLICK_ID_PARAMS) {
		const value = parseQueryValue(query[param]);
		if (value && !looksLikePii(value)) {
			parsed[param] = value;
		}
	}

	return parsed;
}

export function parseUtmFromQuery(query: QueryRecord): UtmAttribution {
	const parsed = parseLandingFromQuery(query);
	const utms: UtmAttribution = {};

	for (const param of UTM_PARAMS) {
		if (parsed[param]) {
			utms[param] = parsed[param];
		}
	}

	return utms;
}

export function getStoredLandingAttribution(): LandingAttribution {
	if (!isBrowser()) return {};

	try {
		const stored = getLocalStorage(ATTRIBUTION_KEY);
		if (stored && typeof stored === 'object') {
			return stored as LandingAttribution;
		}

		const legacy = sessionStorage.getItem(ATTRIBUTION_KEY);
		return legacy ? (JSON.parse(legacy) as LandingAttribution) : {};
	} catch {
		return {};
	}
}

export function getStoredUtmAttribution(): UtmAttribution {
	const stored = getStoredLandingAttribution();
	const utms: UtmAttribution = {};

	for (const param of UTM_PARAMS) {
		if (stored[param]) {
			utms[param] = stored[param];
		}
	}

	return utms;
}

export function captureUtmAttribution(query: QueryRecord): LandingAttribution {
	if (!isBrowser() || !isAttributionEnabledEnv()) {
		return getStoredLandingAttribution();
	}

	const existing = getStoredLandingAttribution();
	const incoming = parseLandingFromQuery(query);
	const next: LandingAttribution = { ...existing };

	if (Object.keys(existing).length === 0 || !hasFirstTouchClickIds(existing)) {
		for (const [key, value] of Object.entries(incoming)) {
			if (value && !next[key as keyof LandingAttribution]) {
				next[key as keyof LandingAttribution] = value;
			}
		}
	}

	if (!next.landing_page) {
		next.landing_page = window.location.href;
	}

	if (!next.referrer) {
		const ref = document.referrer;
		if (ref && ref !== window.location.href) {
			next.referrer = ref;
		}
	}

	if (next.fbclid && !next.fbc) {
		const cookieFbc = getCookie('_fbc');
		next.fbc = cookieFbc ?? `fb.1.${Date.now()}.${next.fbclid}`;
	}

	if (Object.keys(next).length === 0) {
		return {};
	}

	setLocalStorage(ATTRIBUTION_KEY, next);
	ensureLeadJourneyId();
	persistSignupAttributionCookie();

	return next;
}

function hasFirstTouchClickIds(stored: LandingAttribution): boolean {
	return (
		UTM_PARAMS.some((param) => !!stored[param]) ||
		!!stored.gclid ||
		!!stored.fbclid
	);
}

export const ATTRIBUTION_ENABLED_ENVS = ['production'];

export function isAttributionEnabledEnv(): boolean {
	return ATTRIBUTION_ENABLED_ENVS.includes(
		process.env.NEXT_PUBLIC_SERVER || ''
	);
}

function persistAttributionCookie(attribution: SignupAttribution): void {
	if (!isBrowser()) return;

	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	document.cookie = `${ATTRIBUTION_COOKIE}=${encodeURIComponent(
		JSON.stringify(attribution)
	)}; Path=/; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax${secure}`;
}

export function parseAttributionCookie(
	raw: string | undefined
): SignupAttribution | undefined {
	if (!raw) return undefined;

	try {
		const decoded = raw.startsWith('%') ? decodeURIComponent(raw) : raw;
		const parsed = JSON.parse(decoded) as SignupAttribution;
		if (parsed && typeof parsed === 'object') {
			return parsed;
		}
	} catch {
		try {
			const parsed = JSON.parse(decodeURIComponent(raw)) as SignupAttribution;
			if (parsed && typeof parsed === 'object') {
				return parsed;
			}
		} catch {
			return undefined;
		}
	}

	return undefined;
}

export function getAttributionForApi(): SignupAttribution | undefined {
	if (!isAttributionEnabledEnv()) return undefined;
	if (!isBrowser()) return undefined;

	const attribution = getSignupAttribution();
	persistAttributionCookie(attribution);
	return attribution;
}

export function persistSignupAttributionCookie(): void {
	if (!isAttributionEnabledEnv() || !isBrowser()) return;
	persistAttributionCookie(getSignupAttribution());
}

export function getSignupAttribution(): SignupAttribution {
	const stored = getStoredLandingAttribution();
	const cookieFbc = getCookie('_fbc');
	const fbclid = stored.fbclid;
	const fbc =
		cookieFbc ??
		stored.fbc ??
		(fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined);

	const attribution: SignupAttribution = {
		...stored,
		consent_granted: getLocalStorage('cookie_consent') === true,
	};

	const gaClientId = parseGaClientId(getCookie('_ga'));
	if (gaClientId) {
		attribution.ga_client_id = gaClientId;
	}

	const fbp = getCookie('_fbp');
	if (fbp) {
		attribution.fbp = fbp;
	}

	if (fbc) {
		attribution.fbc = fbc;
	}

	return attribution;
}

export function ensureLeadJourneyId(): string {
	if (!isBrowser()) return '';

	let id = sessionStorage.getItem(JOURNEY_ID_KEY);
	if (!id) {
		id =
			typeof crypto !== 'undefined' && crypto.randomUUID
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random()}`;
		sessionStorage.setItem(JOURNEY_ID_KEY, id);
	}

	return id;
}

export function getLeadJourneyId(): string | undefined {
	if (!isBrowser()) return undefined;
	return sessionStorage.getItem(JOURNEY_ID_KEY) ?? undefined;
}

export function setLeadQualificationOutcome(
	qualified: boolean,
	caseId: string
): void {
	if (!isBrowser()) return;

	sessionStorage.setItem(
		QUALIFICATION_OUTCOME_KEY,
		qualified ? 'qualified' : 'not_qualified'
	);
	sessionStorage.setItem(JOURNEY_CASE_ID_KEY, caseId);
}

export function getLeadQualificationOutcome():
	| 'qualified'
	| 'not_qualified'
	| undefined {
	if (!isBrowser()) return undefined;

	const value = sessionStorage.getItem(QUALIFICATION_OUTCOME_KEY);
	if (value === 'qualified' || value === 'not_qualified') {
		return value;
	}

	return undefined;
}

export function getLeadJourneyCaseId(): string | undefined {
	if (!isBrowser()) return undefined;
	return sessionStorage.getItem(JOURNEY_CASE_ID_KEY) ?? undefined;
}
